<!-- src/components/clinical/ImageGallery.vue -->
<template>
    <div class="image-gallery">
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>
      
      <div v-else-if="error" class="alert alert-danger">
        <i class="bi bi-exclamation-triangle-fill me-2"></i>
        {{ error }}
      </div>
      
      <div v-else-if="images.length === 0" class="text-center py-4 text-muted">
        <i class="bi bi-images display-4"></i>
        <p class="mt-2">No hay imágenes disponibles</p>
      </div>
      
      <div v-else>
        <!-- Image filter controls -->
        <div class="mb-3 d-flex justify-content-between align-items-center">
          <div class="btn-group" role="group">
            <button 
              type="button" 
              class="btn" 
              :class="activeFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'" 
              @click="setFilter('all')"
            >
              Todas
            </button>
            <button 
              type="button" 
              class="btn" 
              :class="activeFilter === 'photo' ? 'btn-primary' : 'btn-outline-primary'" 
              @click="setFilter('photo')"
            >
              Fotografías
            </button>
            <button 
              type="button" 
              class="btn" 
              :class="activeFilter === 'xray' ? 'btn-primary' : 'btn-outline-primary'" 
              @click="setFilter('xray')"
            >
              Radiografías
            </button>
            <button 
              type="button" 
              class="btn" 
              :class="activeFilter === 'panoramic' ? 'btn-primary' : 'btn-outline-primary'" 
              @click="setFilter('panoramic')"
            >
              Panorámicas
            </button>
          </div>
        </div>
        
        <!-- Image grid -->
        <div class="row row-cols-1 row-cols-md-3 g-3">
          <div v-for="image in filteredImages" :key="image.id" class="col">
            <div class="card h-100">
              <img 
                :src="image.image" 
                class="card-img-top" 
                :alt="image.description || 'Imagen clínica'"
                @click="showImageModal(image)"
              >
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-start">
                  <span class="badge bg-info">{{ getImageTypeLabel(image.image_type) }}</span>
                  <small class="text-muted">{{ formatDate(image.upload_date) }}</small>
                </div>
                <p v-if="image.description" class="card-text mt-2">{{ image.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Image modal -->
      <div class="modal fade" :class="{ 'show d-block': showModal }" tabindex="-1">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                {{ selectedImage ? getImageTypeLabel(selectedImage.image_type) : '' }}
              </h5>
              <button type="button" class="btn-close" @click="hideImageModal"></button>
            </div>
            <div class="modal-body">
              <img 
                v-if="selectedImage" 
                :src="selectedImage.image" 
                :alt="selectedImage.description || 'Imagen clínica'"
                class="img-fluid"
              >
              <p v-if="selectedImage && selectedImage.description" class="mt-3">
                {{ selectedImage.description }}
              </p>
              <p v-if="selectedImage" class="text-muted mt-2">
                Subido: {{ formatDate(selectedImage.upload_date) }}
              </p>
            </div>
            <div class="modal-footer">
              <button 
                v-if="selectedImage && canDelete" 
                type="button" 
                class="btn btn-danger me-auto" 
                @click="confirmDeleteImage"
              >
                <i class="bi bi-trash me-1"></i> Eliminar
              </button>
              <button type="button" class="btn btn-secondary" @click="hideImageModal">Cerrar</button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Modal backdrop -->
      <div v-if="showModal" class="modal-backdrop fade show"></div>
      
      <!-- Delete confirmation modal -->
      <div class="modal fade" :class="{ 'show d-block': showDeleteConfirm }" tabindex="-1">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Confirmar eliminación</h5>
              <button type="button" class="btn-close" @click="showDeleteConfirm = false"></button>
            </div>
            <div class="modal-body">
              <p>¿Estás seguro que deseas eliminar esta imagen? Esta acción no se puede deshacer.</p>
            </div>
            <div class="modal-footer">
              <button 
                type="button" 
                class="btn btn-secondary" 
                @click="showDeleteConfirm = false"
              >
                Cancelar
              </button>
              <button 
                type="button" 
                class="btn btn-danger" 
                @click="deleteImage"
                :disabled="deleting"
              >
                <span v-if="deleting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Eliminar
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Second modal backdrop -->
      <div v-if="showDeleteConfirm" class="modal-backdrop fade show"></div>
    </div>
  </template>
  
  <script>
import { clinicalImageService } from '@/services/api';

  export default {
    name: 'ImageGallery',
    props: {
      clinicalHistoryId: {
        type: [Number, String],
        required: true
      },
      canDelete: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        images: [],
        loading: true,
        error: null,
        activeFilter: 'all',
        showModal: false,
        selectedImage: null,
        showDeleteConfirm: false,
        deleting: false
      };
    },
    computed: {
      filteredImages() {
        if (this.activeFilter === 'all') {
          return this.images;
        }
        return this.images.filter(image => image.image_type === this.activeFilter);
      }
    },
    async created() {
      await this.fetchImages();
    },
    methods: {
      async fetchImages() {
        this.loading = true;
        this.error = null;
        try {
        const response = await clinicalImageService.getByClinicalHistory(this.clinicalHistoryId);
        this.images = response.data;
      } catch (error) {
        console.error('Error fetching images:', error);
        this.error = 'Error al cargar las imágenes. Por favor, intenta de nuevo.';
      } finally {
        this.loading = false;
      }
      },
      setFilter(filter) {
        this.activeFilter = filter;
      },
      getImageTypeLabel(type) {
        const types = {
          'photo': 'Fotografía',
          'xray': 'Radiografía',
          'panoramic': 'Panorámica',
          'other': 'Otro'
        };
        return types[type] || 'Imagen';
      },
      formatDate(dateString) {
        if (!dateString) return '';
        
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        });
      },
      showImageModal(image) {
        this.selectedImage = image;
        this.showModal = true;
      },
      hideImageModal() {
        this.showModal = false;
        this.selectedImage = null;
      },
      confirmDeleteImage() {
        this.showDeleteConfirm = true;
      },
      async deleteImage() {
        if (!this.selectedImage) return;
        
        this.deleting = true;
        
        try {
            await clinicalImageService.delete(this.selectedImage.id);
          
          // Remove from list
          this.images = this.images.filter(img => img.id !== this.selectedImage.id);
          
          // Close modals
          this.showDeleteConfirm = false;
          this.showModal = false;
          this.selectedImage = null;
          
          // Show success message
          this.$store.dispatch('notification/success', 'Imagen eliminada correctamente');
        } catch (error) {
          console.error('Error deleting image:', error);
          this.$store.dispatch('notification/error', 'Error al eliminar la imagen. Por favor, intenta de nuevo.');
        } finally {
          this.deleting = false;
        }
      }
    }
  };
  </script>
  
  <style scoped>
  .card-img-top {
    height: 180px;
    object-fit: cover;
    cursor: pointer;
    transition: transform 0.2s;
  }
  
  .card-img-top:hover {
    transform: scale(1.03);
  }
  
  .modal {
    background-color: rgba(0, 0, 0, 0.5);
  }
  
  .modal-backdrop + .modal-backdrop {
    z-index: 1060;
  }
  </style>